import { lazy, Suspense } from 'react';
import { Link } from "react-router-dom";
import CookieConsent from "react-cookie-consent";
import PageSeo from '../components/PageSeo';
import HomeIntroVideo from '../components/HomeIntroVideo';
import Slide from './Slide';
import RouteFallback from '../components/RouteFallback';
import { HOME_FOCUS_AREAS, SERVICE_QUICK_NAV } from '../data/homePageServices';
import {
  FaArrowRight,
  FaComments,
  FaMicrophoneAlt,
  FaMobileAlt,
  FaNodeJs,
  FaPython,
  FaReact,
  FaRobot,
  FaWordpress,
} from 'react-icons/fa';
import { SiNextdotjs } from 'react-icons/si';

import '../styles/home-hero.css';
import '../styles/home-problems.css';
import '../styles/home-focus.css';

const HomeSpecialtyCompact = lazy(() => import('../components/home/HomeSpecialtyCompact'));
const HomeIndustriesPreview = lazy(() => import('../components/home/HomeIndustriesPreview'));
const Ourproject = lazy(() => import('../components/Ourproject'));
const HomeClientVoices = lazy(() => import('../components/home/HomeClientVoices'));
const HomeBrandsSection = lazy(() => import('../components/home/HomeBrandsSection'));
const Homeblogslider = lazy(() => import('./Homeblogslider'));
const Newsletters = lazy(() => import('./Newsletters'));

const HOME_FOCUS_ICON_MAP = {
  'AI solutions & automation': FaRobot,
  'AI chatbot development': FaComments,
  'AI voice assistants': FaMicrophoneAlt,
  'Python development': FaPython,
  'Node.js backend engineering': FaNodeJs,
  'Next.js web applications': SiNextdotjs,
  'React frontend development': FaReact,
  'React Native mobile apps': FaMobileAlt,
  'Roots Sage WordPress themes': FaWordpress,
};

const Home = () => {
  return (
    <>
      <PageSeo path="/" />
      <div>
        <div className="content-wrapper">
          <div className="header-spacer" />
          <Slide />

          <section className="home-post-hero home-surface--dark" aria-labelledby="home-intro-heading">
            <div className="container">
              <div className="home-post-hero__layout">
                <div className="home-post-hero__content">
                  <p className="home-post-hero__eyebrow">Web development &amp; product engineering agency</p>
                  <h2 id="home-intro-heading" className="home-post-hero__title">
                    Plux Infosystems builds websites, products, and connected operations for global teams.
                  </h2>
                  <p className="home-post-hero__lead">
                    We are a web development and product engineering partner for companies that need React and Next.js
                    front ends, reliable APIs, Magento and ecommerce systems, WordPress/Sage CMS platforms, AI
                    automation, and ongoing maintenance—delivered with clear scope, strong UX, and production-ready
                    integrations.
                  </p>
                  <ul className="home-post-hero__service-lines" aria-label="Core service strengths">
                    <li>
                      <span className="home-post-hero__chip-icon" aria-hidden="true">W</span>
                      <div>
                        <strong>Web and product engineering</strong>
                        <small>React, Next.js, Python, Node.js, and scalable architecture</small>
                      </div>
                    </li>
                    <li>
                      <span className="home-post-hero__chip-icon" aria-hidden="true">E</span>
                      <div>
                        <strong>Ecommerce and Magento</strong>
                        <small>Storefronts, checkout, integrations, and operations tooling</small>
                      </div>
                    </li>
                    <li>
                      <span className="home-post-hero__chip-icon" aria-hidden="true">AI</span>
                      <div>
                        <strong>AI automation</strong>
                        <small>Chatbots, workflows, and CRM-connected systems</small>
                      </div>
                    </li>
                    <li>
                      <span className="home-post-hero__chip-icon" aria-hidden="true">S</span>
                      <div>
                        <strong>Support and optimization</strong>
                        <small>Performance, hosting, updates, and long-term care</small>
                      </div>
                    </li>
                  </ul>
                  <nav className="home-post-hero__actions" aria-label="Primary actions">
                    <Link to="/getintouch" className="home-post-hero__btn home-post-hero__btn--primary">
                      Book a consultation
                    </Link>
                    <Link to="/web-development" className="home-post-hero__btn home-post-hero__btn--secondary">
                      Web development services
                    </Link>
                    <Link to="/services" className="home-post-hero__btn home-post-hero__btn--outline">
                      All services
                    </Link>
                  </nav>
                </div>
                <div className="home-post-hero__visual" aria-label="Delivery approach preview">
                  <div className="home-growth-dashboard home-growth-dashboard--simple">
                    <div className="home-growth-dashboard__topbar">
                      <span className="home-growth-dashboard__dot home-growth-dashboard__dot--teal" />
                      <span className="home-growth-dashboard__dot home-growth-dashboard__dot--amber" />
                      <span className="home-growth-dashboard__dot" />
                      <strong>How we deliver</strong>
                    </div>
                    <div className="home-growth-dashboard__screen home-growth-dashboard__screen--simple">
                      <p className="home-growth-dashboard__simple-copy">
                        We align on goals, ship in modern stacks, connect your tools, and improve speed, conversion,
                        and reliability after launch.
                      </p>
                    </div>
                    <div className="home-post-hero__video-card">
                      <p>Watch our delivery approach</p>
                      <HomeIntroVideo />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="home-visitor-questions home-surface--light" aria-labelledby="visitor-questions-heading">
            <div className="container">
              <div className="home-visitor-questions__shell">
                <div className="home-visitor-questions__header">
                  <div>
                    <p className="home-visitor-questions__eyebrow">Problems we solve</p>
                    <h2 id="visitor-questions-heading" className="home-visitor-questions__title">
                      Turn website friction into a clear improvement plan.
                    </h2>
                    <p className="home-visitor-questions__intro">
                      Slow pages, disconnected tools, fragile ecommerce flows, and manual operations all hurt conversion.
                      We connect the business problem to the technical fix.
                    </p>
                  </div>
                  <aside className="home-visitor-questions__review-card" aria-label="Project review approach">
                    <span>Project review</span>
                    <strong>Audit the blocker. Prioritize the fix. Ship the improvement.</strong>
                    <p>Performance, ecommerce, integrations, and automation reviewed as one delivery path.</p>
                  </aside>
                </div>
                <div className="home-visitor-questions__flow" aria-label="Common website problems and fixes">
                  {[
                    {
                      title: 'Slow pages lose visitors',
                      impact: 'Lower rankings, weaker conversion, and higher ad waste.',
                      fix: 'We audit code, assets, hosting, and Core Web Vitals to remove bottlenecks.',
                    },
                    {
                      title: 'Ecommerce operations feel fragile',
                      impact: 'Checkout, catalog, and admin issues slow daily revenue work.',
                      fix: 'We improve checkout, catalog UX, integrations, and admin workflows.',
                    },
                    {
                      title: 'Systems do not connect',
                      impact: 'Leads, orders, analytics, and support data get stuck in silos.',
                      fix: 'We connect CRM, forms, ecommerce, analytics, and internal tools.',
                    },
                    {
                      title: 'Manual work repeats daily',
                      impact: 'Teams spend too much time copying data and answering repeat questions.',
                      fix: 'We design AI-assisted workflows for support, leads, and operations.',
                    },
                  ].map((item, index) => (
                    <article key={item.title} className="home-visitor-questions__item">
                      <span className="home-visitor-questions__number">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div>
                        <p className="home-visitor-questions__tag">Challenge</p>
                        <h3 className="home-visitor-questions__item-title">{item.title}</h3>
                        <p>{item.impact}</p>
                        <div className="home-visitor-questions__fix">
                          <span>Fix</span>
                          <p>{item.fix}</p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
                <nav className="home-visitor-questions__actions" aria-label="Request a review">
                  <Link to="/getintouch" className="home-visitor-questions__cta home-visitor-questions__cta--primary">
                    Request a project review
                  </Link>
                  <Link to="/website-maintenance" className="home-visitor-questions__cta home-visitor-questions__cta--secondary">
                    Explore maintenance services
                  </Link>
                </nav>
              </div>
            </div>
          </section>

          <section className="home-focus-section home-surface--light" aria-labelledby="home-focus-heading">
            <div className="home-focus-section__shell">
              <div className="home-focus-section__intro">
                <div className="heading home-focus-section__header">
                  <p className="home-focus-section__kicker">Core services</p>
                  <h2 id="home-focus-heading" className="h2 heading-title">
                    Web development, ecommerce, AI automation, and CMS from one team
                  </h2>
                  <p className="heading-text c-dark home-focus-section__sub">
                    Choose a focused service page for full scope, deliverables, and FAQs—or start with a consultation
                    and we will recommend the right mix of engineering, integrations, and support.
                  </p>
                </div>
              </div>
              <div className="home-focus-grid">
                {HOME_FOCUS_AREAS.map((item) => {
                  const Icon = HOME_FOCUS_ICON_MAP[item.title] || FaRobot;
                  return (
                    <Link to={item.to} className="home-focus-card" key={item.title}>
                      <div className="home-focus-card__topline">
                        <span className="home-focus-card__spark" aria-hidden="true">
                          <Icon />
                        </span>
                        <div className="home-focus-card__icon">
                          <img src={item.img} alt={item.alt} width={56} height={56} loading="lazy" />
                        </div>
                      </div>
                      <h3 className="home-focus-card__title">{item.title}</h3>
                      <p className="home-focus-card__desc">{item.desc}</p>
                      <span className="home-focus-card__link">
                        Explore <FaArrowRight aria-hidden="true" />
                      </span>
                    </Link>
                  );
                })}
              </div>
              <nav className="home-quick-nav" aria-label="Popular service pages">
                {SERVICE_QUICK_NAV.map((item) => (
                  <Link key={item.to} to={item.to} className="home-quick-nav__link">
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>
          </section>

          <Suspense fallback={<RouteFallback />}>
            <HomeSpecialtyCompact />
            <HomeIndustriesPreview />
            <Ourproject />
            <HomeClientVoices />
            <HomeBrandsSection />
            <Homeblogslider />
            <Newsletters />
          </Suspense>

          <CookieConsent
            location="bottom"
            buttonText="Accept"
            declineButtonText="Decline"
            enableDeclineButton
            cookieName="pluxCookieConsent"
            style={{ background: "var(--brand-ink-soft)", textAlign: "center", fontSize: "16px" }}
            buttonStyle={{ background: "var(--brand-teal-button)", color: "var(--brand-white)", fontSize: "13px" }}
            expires={150}
          >
            We use cookies to improve your experience on this site. Accept to allow analytics and preference cookies,
            or decline to continue with essential cookies only.
          </CookieConsent>
        </div>
      </div>
    </>
  );
};

export default Home;
